import { yupResolver } from "@hookform/resolvers/yup";
import { Link, Stack, Typography } from "@mui/material";
import React, { ReactNode, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { QueryClient, useQueryClient } from "react-query";
import * as yup from "yup";

import { AccountsKey, fetchPromoCode } from "../../queries/accounts";
import { FormTextInput } from "../form/FormTextInput";

const formSchema = yup.object({
    tags: yup.object({
        promo_code: yup
            .string()
            .label("Invite Code")
            .required()
            .test({
                name: "is-valid-code",
                message: "Invalid invite code",
                test: async (value, { options }) => {
                    const { queryClient } = options.context as IWelcomeFormContext;

                    if (value) {
                        const { valid } = await queryClient.fetchQuery(AccountsKey.promoCode(value), () =>
                            fetchPromoCode(value),
                        );
                        return valid;
                    }

                    return true;
                },
            }),
    }),
});

export interface IWelcomeFormData {
    tags: {
        promo_code?: string;
    };
}

interface IWelcomeFormContext {
    queryClient: QueryClient;
}

interface IWelcomePageProps {
    defaultValues?: IWelcomeFormData;
    nextPageButton?: ReactNode;
    onSubmit: SubmitHandler<IWelcomeFormData>;
}

export const WelcomePage: React.FC<IWelcomePageProps> = ({ defaultValues, nextPageButton, onSubmit }) => {
    const queryClient = useQueryClient();

    const form = useForm<IWelcomeFormData, IWelcomeFormContext>({
        mode: "onBlur",
        defaultValues,
        context: { queryClient },
        resolver: yupResolver(formSchema),
    });

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        if (defaultValues?.tags.promo_code != null) {
            form.trigger("tags.promo_code");
        }
    }, [defaultValues?.tags.promo_code, form]);

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <Typography variant="h1" sx={{ my: 2 }}>
                        Welcome to Levro!
                    </Typography>
                    <Typography textAlign="justify">
                        We are excited to have you on board. Before we can open your account, we just need a few pieces
                        of information (listed below). This signup form should only take 5-15 minutes, depending on your
                        business. Our application process validates the information you provide against government
                        databases to skip unnecessary uploads of documents.
                    </Typography>
                    <Typography textAlign="justify">
                        Documents you might need to provide for you and beneficial owners (people who own 25%+ of your
                        company):
                    </Typography>
                    <ul>
                        <Typography component="li">ID verification (passport or US drivers license)</Typography>
                        <Typography component="li">
                            EIN verification (via a letter issued by the IRS) or your EIN application (SS4)
                        </Typography>
                        <Typography component="li">
                            Address verification (via a utility bill or bank statement)
                        </Typography>
                        <Typography component="li">Articles of Incorporation for your US entity</Typography>
                    </ul>
                    <FormTextInput
                        label="Invite Code"
                        name="tags.promo_code"
                        helperText={
                            <>
                                Please enter your invite code. If you don&apos;t have one, you can{" "}
                                <Link href="https://levro.com/onboarding-form" rel="noreferrer">
                                    request an invite
                                </Link>
                                .
                            </>
                        }
                        required={true}
                    />
                    <Stack alignItems="center">{nextPageButton}</Stack>
                </Stack>
            </form>
        </FormProvider>
    );
};
